import { useCallback } from 'react';
import { ConcurrentTenancyCheckResultDTO } from '@reposit/api-client';
import { analyzeConcurrentTenancy } from './concurrentTenancyUtils';

interface UseAddTenantFormOptions {
  checkResult: ConcurrentTenancyCheckResultDTO | null;
  checkError: string | null;
  checkedEmail: string | null;
  isChecking: boolean;
  checkConcurrentTenancy: (email: string) => void;
  addTenantDirectly: (email: string) => void;
}

export const useAddTenantForm = ({
  checkResult,
  checkError,
  checkedEmail,
  isChecking,
  checkConcurrentTenancy,
  addTenantDirectly,
}: UseAddTenantFormOptions) => {
  const handleFormSubmit = useCallback(
    (values: { newTenantEmail: string }) => {
      const isCurrentEmailChecked = values.newTenantEmail === checkedEmail;

      // Check if this email has a blocked result (not an error)
      if (checkResult && isCurrentEmailChecked && !checkError) {
        const analysis = analyzeConcurrentTenancy(checkResult, values.newTenantEmail);
        // If user has already seen a blocked result and clicked "Confirm anyway", proceed immediately
        if (analysis.isBlocked) {
          addTenantDirectly(values.newTenantEmail);
          return;
        }
      }

      // Otherwise, run the concurrency check (or retry if there was an error)
      checkConcurrentTenancy(values.newTenantEmail);
    },
    [checkResult, checkError, checkedEmail, addTenantDirectly, checkConcurrentTenancy]
  );

  const getButtonLabel = useCallback(
    (email: string): string => {
      const isCurrentEmailChecked = email === checkedEmail;

      if (isChecking) {
        return 'Checking...';
      }

      if (checkError && isCurrentEmailChecked) {
        return 'Retry';
      }

      if (checkResult && isCurrentEmailChecked && !checkError) {
        const analysis = analyzeConcurrentTenancy(checkResult, email);
        if (analysis.isBlocked) {
          return 'Confirm anyway';
        }
      }

      return 'Confirm';
    },
    [isChecking, checkError, checkResult, checkedEmail]
  );

  return {
    handleFormSubmit,
    getButtonLabel,
  };
};
